import { compact, get, first } from 'lodash';
import { formatDateHyphenatedByLocale } from 'util/formatters/dateFormatter';
import { logError } from 'api/errors';
import { inferUnit } from 'util/unitConversion';
import moment from 'moment';

// constants
import { scriptTypes, applicationTypes } from 'constants/scripts';
import { UNIT_TYPES } from 'constants/unitTypes';
import { cropIds } from 'constants/crops';

// selectors
import { getActiveFieldCountryCode, getActiveFieldUuid, getActiveScriptType } from 'current/selectors';
import { getFieldByUuid } from 'fields/selectors';
import {
  getProductById
} from 'products/selectors';
import {
  substanceTypeIsNitrogen
} from 'products/fertility-selectors';
import { getApplicationTypeForScriptType } from 'scripts/selectors';
import { getPlantingPlanParamsValidationResult } from 'validations/selectors';
import { getIncludedLayersParamForRecommendationsRequest } from 'advanced/selectors';
import { getNominalWeightByCropId } from 'crops/selectors';

// utils
import { locale } from '@tcc/i18n';
import { kebabCase } from 'util/string';

export const getPlantingPlanParams = (state) => get(state, 'plantingPlan.params');
export const canUpdatePlantingPlan = (state) => {
  return !get(getPlantingPlanParamsValidationResult(state), 'isInvalid', false);
};

export const getPlantingPlanParamsForTemplateCall = (state, agronomicSeason) => {
  const plantingPlanParams = getPlantingPlanParams(state);

  return {
    activityUuid: plantingPlanParams.activityUuid,

    // The only supported crop for creating advanced rx. If multiple crops supported in future, then it can be passed
    // through planting params and returned here.
    cropId: cropIds.CORN,

    // TODO: When you change crop ^ to support more cropIds, change this one as well
    cropNominalWeight: getNominalWeightByCropId(state, cropIds.CORN),

    date: plantingPlanParams.date,
    defaultScriptName: plantingPlanParams.defaultScriptName,
    goalRatio: parseInt(plantingPlanParams['goal-ratio'], 10),
    grainPrice: { q: get(plantingPlanParams, 'price-commodity', 0) || 0, u: `usd/${inferUnit('mass.yield').unitKey}` },
    numZones: plantingPlanParams['num-zones'],
    productId: plantingPlanParams.productId,
    seasonCode: plantingPlanParams.seasonCode,
    seedCost: { q: get(plantingPlanParams, 'price-product', 0) || 0, u: UNIT_TYPES.usd_bag },
    targetYield: { q: get(plantingPlanParams, 'target-yield', 0) || 0, u: inferUnit('area-density.yield').unitKey },
    include: getIncludedLayersParamForRecommendationsRequest(state),
    countryCode: plantingPlanParams.countryCode || getActiveFieldCountryCode(state),
    agronomicSeason: agronomicSeason || plantingPlanParams.agronomicSeason,
    // Most values default to 0 when they are empty when they truly should be undefined when there is no user input.
    currentAveragePopulation: {
      q: plantingPlanParams?.currentAveragePopulation && Number(plantingPlanParams.currentAveragePopulation),
      u: inferUnit('seed-density').unitKey
    }
  };
};

export const hasPlantingPlanScriptName = (state) => !!get(getPlantingPlanParams(state), 'scriptName');
export const hasPlantingPlanSubstanceType = (state) => !!get(getPlantingPlanParams(state), 'substanceTypeUuid');
export const plantingPlanSubstanceTypeIsNitrogen = (state) => {
  const substanceTypeUuid = get(getPlantingPlanParams(state), 'substanceTypeUuid');
  return substanceTypeUuid ? substanceTypeIsNitrogen(state, substanceTypeUuid) : false;
};
export const getDefaultScriptName = (state, plantingPlanParams) => {
  const scriptType = getActiveScriptType(state);
  const applicationType = getApplicationTypeForScriptType(scriptType);
  const isApplication = applicationType === applicationTypes.APPLICATION;

  plantingPlanParams = plantingPlanParams || getPlantingPlanParams(state);
  const applicationField = isApplication ? applicationTypes.APPLICATIONV2 : applicationType;
  const productIds = get(plantingPlanParams, `products.${applicationField}`, {});
  
  const { name: fieldName } = getFieldByUuid(state, getActiveFieldUuid(state));
  const fieldNameString = kebabCase(fieldName).substr(0, 12);
  let productsString;
  let productId;

  if(isApplication) {
    if(productIds && productIds.length > 1) {
      productsString = 'tank-mix';
    }else{
      productId = first(productIds);
    }
  }else{
    // Advanced flow doesn't store values in store:plantingPlan.products.
    // should change that if/when we allow dual seed for advanced
    productId = Object.values(productIds)[0] || plantingPlanParams.productId;
  }

  if (productId) {
    const product = getProductById({ applicationType, id: productId, state });
    const name = product ? product.name : productId;
    productsString = name.replace(/[^a-zA-Z0-9]/g,'-').substr(0, 8);
  }

  const dateString = formatDateHyphenatedByLocale(new Date(), locale);

  return compact([fieldNameString, productsString, dateString]).join('-');
};

/** @return ISO date string or null if no max date. */
export const getPlantingPlanMaxDate = (state) => {
  const scriptType = getActiveScriptType(state);
  switch (scriptType) {
    case scriptTypes.FERTILITY:
    case scriptTypes.PLANTING:
      // set date in the past to 365 days in the future
      // this will get cleaned up with our season service work
      return moment().add(1,'years').toISOString();
    default:
      logError(`unexpected scriptType ${scriptType}`);
      return null;
  }
};

/** @return ISO date string */
export const getPlantingPlanMinDate = (state) => {
  const scriptType = getActiveScriptType(state);
  switch (scriptType) {
    case scriptTypes.FERTILITY:
      // set date in the past to January 1st of last year
      return moment({ year: moment().year() - 1 }).toISOString();
    case scriptTypes.PLANTING:
      // set date in the past to January 1st of this year
      return moment({ year: moment().year() }).toISOString();
    default:
      logError(`unexpected scriptType ${scriptType}`);
      return null;
  }
};
